import { Fragment, useState } from "react";
import {
  type AdminBroadcastRecord,
  fetchAdminBroadcast,
} from "../../api/admin-communications";
import {
  ErrorBanner,
  LoadingIndicator,
  tableStyles as ts,
} from "../../components/TableParts";
import { t } from "../../i18n";
import {
  formatDate,
  progressText,
  shortId,
  templateLabel,
} from "./communications-formatters";

export function CommunicationsHistory({
  broadcasts,
}: {
  broadcasts: ReadonlyArray<AdminBroadcastRecord>;
}): React.JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminBroadcastRecord | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function selectBroadcast(broadcastId: string): Promise<void> {
    if (selectedId === broadcastId) {
      setSelectedId(null);
      setDetail(null);
      setDetailError(null);
      return;
    }
    setSelectedId(broadcastId);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      setDetail(await fetchAdminBroadcast(broadcastId));
    } catch (err) {
      setDetailError(
        err instanceof Error ? err.message : t("common.failed_to_load"),
      );
    } finally {
      setDetailLoading(false);
    }
  }

  if (broadcasts.length === 0) {
    return <p style={ts.emptyText}>{t("communications.no_broadcasts")}</p>;
  }
  return (
    <table style={ts.table}>
      <thead>
        <tr>
          <th style={ts.th}>{t("communications.col_broadcast")}</th>
          <th style={ts.th}>{t("communications.col_template")}</th>
          <th style={ts.th}>{t("communications.col_status")}</th>
          <th style={ts.th}>{t("communications.col_progress")}</th>
          <th style={ts.th}>{t("communications.col_created")}</th>
          <th style={ts.th}>{t("communications.col_completed")}</th>
          <th style={ts.th}>{t("common.action")}</th>
        </tr>
      </thead>
      <tbody>
        {broadcasts.map((broadcast) => (
          <Fragment key={broadcast.broadcast_id}>
            <BroadcastRow
              broadcast={broadcast}
              selected={selectedId === broadcast.broadcast_id}
              onSelect={() => void selectBroadcast(broadcast.broadcast_id)}
            />
            {selectedId === broadcast.broadcast_id ? (
              <tr>
                <td colSpan={7} style={ts.expandedCell}>
                  <BroadcastDetailPanel
                    detail={detail}
                    error={detailError}
                    loading={detailLoading}
                  />
                </td>
              </tr>
            ) : null}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

function BroadcastRow({
  broadcast,
  selected,
  onSelect,
}: {
  broadcast: AdminBroadcastRecord;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <tr style={ts.row}>
      <td style={ts.td}>
        <div style={styles.mono} title={broadcast.broadcast_id}>
          {shortId(broadcast.broadcast_id)}
        </div>
        <div style={styles.reason}>{broadcast.reason}</div>
      </td>
      <td style={ts.td}>{templateLabel(broadcast.template)}</td>
      <td style={ts.td}>
        <span style={{ ...ts.badge, color: statusColor(broadcast.status) }}>
          {broadcast.status}
        </span>
        {broadcast.error && <div style={styles.error}>{broadcast.error}</div>}
      </td>
      <td style={ts.td}>
        <span style={styles.mono}>{progressText(broadcast.progress)}</span>
      </td>
      <td style={ts.td}>{formatDate(broadcast.created_at)}</td>
      <td style={ts.td}>{formatDate(broadcast.completed_at)}</td>
      <td style={ts.td}>
        <button type="button" onClick={onSelect} style={ts.actionBtn}>
          {selected ? t("common.hide_details") : t("common.show_details")}
        </button>
      </td>
    </tr>
  );
}

function BroadcastDetailPanel({
  detail,
  error,
  loading,
}: {
  detail: AdminBroadcastRecord | null;
  error: string | null;
  loading: boolean;
}): React.JSX.Element {
  return (
    <div style={ts.detailPanel}>
      {loading ? <LoadingIndicator /> : null}
      {error ? <ErrorBanner message={error} /> : null}
      {detail ? <BroadcastDetail detail={detail} /> : null}
    </div>
  );
}

function BroadcastDetail({
  detail,
}: {
  detail: AdminBroadcastRecord;
}): React.JSX.Element {
  return (
    <div style={styles.detailGrid}>
      <DetailItem
        label={t("communications.detail_requested_by")}
        value={detail.requested_by}
      />
      <DetailItem
        label={t("communications.detail_updated")}
        value={formatDate(detail.updated_at)}
      />
      <DetailItem
        label={t("communications.detail_audience")}
        value={formatJson(detail.audience)}
      />
      <DetailItem
        label={t("communications.detail_params")}
        value={formatJson(detail.params)}
      />
    </div>
  );
}

function DetailItem({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div>
      <div style={styles.detailLabel}>{label}</div>
      <pre style={styles.detailValue}>{value}</pre>
    </div>
  );
}

function formatJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function statusColor(status: string): string {
  if (status === "done") return "var(--color-success)";
  if (status === "failed") return "var(--color-danger)";
  if (status === "done_partial") return "#ca8a04";
  return "var(--color-text-secondary)";
}

const styles: Record<string, React.CSSProperties> = {
  mono: { fontFamily: "var(--font-mono)", fontSize: 12 },
  reason: {
    marginTop: 4,
    maxWidth: 340,
    color: "var(--color-text-secondary)",
    fontSize: 12,
  },
  error: { marginTop: 4, color: "var(--color-danger)", fontSize: 12 },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
  },
  detailLabel: {
    marginBottom: 4,
    color: "var(--color-text-secondary)",
    fontSize: 12,
    fontWeight: 700,
  },
  detailValue: {
    margin: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: "var(--color-text)",
    fontFamily: "var(--font-mono)",
    fontSize: 12,
  },
};
